import { useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { UilAngleLeft as CloseIcon, UilUpload } from "@iconscout/react-unicons";

import Chip from "@buildappeal/react-component-library/dist/Chip";
import { isBrowser } from "utils/helpers";
import Picture from "./picture";
import IconBtn from "./icon-btn";
import CoreContainer from "./core-container";
import GalleryBaseModal from "./gallery-base-modal";
import GalleryMixedGrid from "./gallery-mixed-grid";

const GalleryProjectModal = ({
  mRef,
  rooms,
  activePic,
  closeModal,
  picsByRoom,
  setActivePic,
  onPictureClick,
  modalScrollableNode,
}) => {
  const { address, sqft, beds, baths, rooms: projRooms, spaces } = activePic?.project || {};

  const addressLine = address ? [address.city, address.state].filter(Boolean) : null;
  const allRoomsCount = projRooms ? `${projRooms} rooms` : null;
  const eachRoomCount = [beds && `${beds} bedrooms`, baths && `${baths} bathrooms`].filter(Boolean);

  useEffect(() => {
    if (isBrowser) {
      document.documentElement.classList.add("!overflow-visible");
    }

    return () => {
      if (isBrowser) {
        document.body.style.overflow = "unset";
        document.documentElement.style.overflow = "unset";
        document.documentElement.classList.remove("!overflow-visible");
      }
    };
  }, []);

  useEffect(() => {
    if (isBrowser) {
      document.body.style.overflow = activePic ? "hidden" : "unset";
    }
  }, [activePic]);

  return (
    <GalleryBaseModal
      fullScreen
      className={cx(!!activePic ? "animate-slide-up" : "animate-slide-down")}
      containerClassName={cx(!!activePic ? "animate-fade-in" : "animate-fade-out")}
      isOpen={!!activePic}
      onClose={closeModal}
      headerLeft={
        <IconBtn
          onClick={closeModal}
          className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-neutral-300"
        >
          <CloseIcon />
        </IconBtn>
      }
      headerRight={
        <IconBtn className="hidden bg-transparent">
          <UilUpload />
        </IconBtn>
      }
    >
      <CoreContainer mRef={mRef} mobilePadding={true} className="mt-12 mb-32">
        <>
          <div className="pl-6">
            <h2 className="text-2xl font-medium md:px-0">Project Overview</h2>
            {!!addressLine && <div className="text-md">{addressLine.join(", ")}</div>}
            {!!(allRoomsCount > 0 || eachRoomCount.length > 0) && (
              <div className="text-mpmd md:text-dpmd">
                {allRoomsCount && <span>{allRoomsCount}&nbsp;</span>}
                {eachRoomCount.length > 0 && <span>- {eachRoomCount.join(", ")}</span>}
              </div>
            )}
            {!!sqft && <div className="text-md">{sqft} sqft</div>}
          </div>
          <div className="-my-2 mt-10 flex flex-nowrap overflow-x-auto px-4 scrollbar-hide md:flex-wrap md:px-0">
            {rooms.map((room) => (
              <div key={room._key} className="w-1/2 flex-[0_0_auto] py-2 px-2 md:w-1/6">
                <div className="shadow-[rgb(0,0,0/18%)_0_2px_4px]">
                  <Picture
                    width={300}
                    height={180}
                    layout="intrinsic"
                    objectFit="cover"
                    className="cursor-pointer"
                    picture={picsByRoom[room._key][0]}
                    alt={picsByRoom[room._key][0]?.alt}
                    title={picsByRoom[room._key][0]?.alt}
                    onClick={() => setActivePic(picsByRoom[room._key][0])}
                  />
                </div>
                <div className="my-2 text-mpmd capitalize md:text-dpmd">{room.spaceName}</div>
              </div>
            ))}
          </div>
          <div className="mt-16 md:mt-32">
            {rooms.map((room) => {
              const space = spaces?.find((s) => s._key === room._key) || room;
              const tags = space?.tags?.length ? space.tags.map((t) => t.label).filter(Boolean) : [];
              return (
                <div
                  key={space?.spaceName}
                  className=" mb-10 flex flex-col  border-b border-neutral-300 pb-6 md:flex-row"
                >
                  <div className="flex-1 px-6">
                    <div className="pb-4  md:sticky md:top-4 md:mb-6 ">
                      <div className="text-2xl font-medium capitalize md:text-2xl">{space?.spaceName}</div>

                      {tags?.length > 0 && (
                        <div className="flex gap-2 py-2">
                          {tags.map((tag, index) => (
                            <Chip key={index} label={tag} size="xs" />
                          ))}
                        </div>
                      )}

                      {!!space?.description && <div className="text-mpmd md:text-dpmd">{space.description}</div>}
                      <div className="text-mpmd md:text-dpmd">
                        {!!space.sqft && <span>Total {space.sqft} sqft&nbsp;</span>}
                        {!!space.measurementsWidth && !!space.measurementsLength && (
                          <span>
                            {!!space.sqft ? "- " : ""}
                            {space.measurementsWidth}&#34; x {space.measurementsLength}&#34; ft &nbsp;
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 md:flex-[2]">
                    <GalleryMixedGrid
                      photos={picsByRoom[room._key]}
                      onClick={onPictureClick}
                      parentNode={modalScrollableNode}
                      activePhoto={activePic?.spaces?.some((s) => s._key === room._key) ? activePic : undefined}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      </CoreContainer>
    </GalleryBaseModal>
  );
};

GalleryProjectModal.propTypes = {
  mRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  closeModal: PropTypes.func,
  picsByRoom: PropTypes.any,
  setActivePic: PropTypes.func,
  onPictureClick: PropTypes.func,
  modalScrollableNode: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  activePic: PropTypes.shape({
    _id: PropTypes.string,
    spaces: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string,
        spaceName: PropTypes.string,
      })
    ),
  }),
  rooms: PropTypes.arrayOf(
    PropTypes.shape({
      _key: PropTypes.string,
      spaceName: PropTypes.string,
    })
  ),
};

export default GalleryProjectModal;
